import { useState, useEffect, useRef, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { Dialog } from "primereact/dialog";
import { ProgressSpinner } from "primereact/progressspinner";
import { getDispositionsByUnfinishedRequestTemplate } from "../../document-templates/DispositionsByUnfinishedRequestTemplate";
import { PdfPreview } from "../../components/PdfPreview/PDFPreview";
import { ButtonInput } from "../../components/ButtonInput/ButtonInput";
import { Request, RequestType } from "../../models/Request";
import { Disposition } from "../../models/Disposition";
import { ProductType } from "../../models/Product";
import { Utility } from "../../core/Utility";
import { getDispositionsByRequest } from "../../services/NewVersionService";
import { getAllProducts } from "../../services/ProductService";
import "./DispositionsByUnfinishedRequest.scss";

export interface DispositionsByUnfinishedRequestProps {
  visible: boolean;
  handleClose: () => void;
  requestId: string;
  buttonInactiveStyle?: React.CSSProperties;
}

export const DispositionsByUnfinishedRequest = (
  p: DispositionsByUnfinishedRequestProps,
) => {
  const { visible, handleClose, requestId, buttonInactiveStyle } = p;

  const { t } = useTranslation();

  const [selectedRequest, setSelectedRequest] = useState<Request>();
  const [dispositionsByRequest, setDispositionsByRequest] = useState<
    Disposition[]
  >([]);
  const [productsLocal, setProductsLocal] = useState<any[]>([]);
  const [processing, setProcessing] = useState(false);
  const [pdfData, setPdfData] = useState<any>();

  const downloadPdfDocument = useRef(null);
  const printPdfDocument = useRef(null);

  const ensureSuccess = (resp: any) => {
    if (resp?.data?.status === "ERROR") {
      throw new Error(resp?.data?.message ?? "Greška");
    }
  };

  useEffect(() => {
    const run = async () => {
      // ne radi ništa ako modal nije otvoren
      if (!visible) return;

      // reset za novi request / novo otvaranje
      setPdfData(undefined);
      setDispositionsByRequest([]);

      if (!requestId) {
        setSelectedRequest(undefined);
        return;
      }

      setProcessing(true);
      try {
        const resp = await getDispositionsByRequest(requestId);
        ensureSuccess(resp);
        const raw = resp?.data?.result ?? resp?.data;
        const list: Disposition[] = Array.isArray(raw) ? raw : [];

        setDispositionsByRequest(list);

        const req = list[0]?.request as Request | undefined;
        setSelectedRequest(req);
      } finally {
        setProcessing(false);
      }
    };

    run();
  }, [visible, requestId]);

  useEffect(() => {
    if (!visible) return;

    const run = async () => {
      try {
        const resp = await getAllProducts();
        ensureSuccess(resp);
        const raw = resp?.data?.result ?? resp?.data;
        const list = Array.isArray(raw) ? raw : [];
        setProductsLocal(list);
      } catch (e) {
        console.error(e);
        setProductsLocal([]);
      }
    };

    run();
  }, [visible]);

  useEffect(() => {
    // štampaj samo kad postoji pdf definicija
    if (!pdfData) return;

    if (printPdfDocument.current) {
      // @ts-ignore
      printPdfDocument.current();
    }
  }, [pdfData]);

  const productById = useMemo(() => {
    const map = new Map<string, any>();
    for (const p of productsLocal ?? []) {
      if (p?.id) map.set(p.id.toString(), p);
    }
    return map;
  }, [productsLocal]);

  const safeJsonArray = (json?: string) => {
    if (!json) return [];
    try {
      const arr = JSON.parse(json);
      return Array.isArray(arr) ? arr : [];
    } catch {
      return [];
    }
  };

  const getReproNames = (disp: Disposition) => {
    const arr = safeJsonArray(
      disp.repromaterialDispositionProductsJSON as string,
    );
    const names = arr
      .map((x: any) => {
        const id = x?.id ?? x?.productId;
        if (!id) return "";
        return productById.get(id.toString())?.name ?? "";
      })
      .filter(Boolean);

    // dok se products ne učita
    if (names.length === 0 && arr.length > 0 && productsLocal.length === 0) {
      return "Učitavanje...";
    }

    return names.join("; ");
  };

  const getReproAmounts = (disp: Disposition) => {
    const arr = safeJsonArray(
      disp.repromaterialDispositionProductsJSON as string,
    );
    const parts = arr
      .map((x: any) => {
        const id = x?.id ?? x?.productId;
        const amount = x?.amount;
        if (!amount) return "";
        const unit = id
          ? (productById.get(id.toString())?.measuringUnit ?? "")
          : "";
        return unit ? `${amount} ${unit}` : `${amount}`;
      })
      .filter(Boolean);

    if (parts.length === 0 && arr.length > 0 && productsLocal.length === 0) {
      return "Učitavanje...";
    }

    return parts.join("; ");
  };

  const getReproNamesArray = (disp: Disposition): string[] => {
    const arr = safeJsonArray(
      disp.repromaterialDispositionProductsJSON as string,
    );
    return arr
      .map((x: any) => {
        const id = x?.id ?? x?.productId;
        if (!id) return "";
        return productById.get(id.toString())?.name ?? "";
      })
      .filter(Boolean);
  };

  const getReproAmountsArray = (disp: Disposition): string[] => {
    const arr = safeJsonArray(
      disp.repromaterialDispositionProductsJSON as string,
    );
    return arr
      .map((x: any) => {
        const id = x?.id ?? x?.productId;
        const amount = x?.amount;
        if (!amount) return "";
        const unit = id
          ? (productById.get(id.toString())?.measuringUnit ?? "")
          : "";
        return unit ? `${amount} ${unit}` : `${amount}`;
      })
      .filter(Boolean);
  };

  let addButtonInactiveStyle: React.CSSProperties = {};
  if (buttonInactiveStyle) addButtonInactiveStyle = buttonInactiveStyle;

  const header = (
    <div className="header">
      <span className="p-dialog-titlebar-icon p-link">
        <i className="bi bi-truck" style={{ fontSize: 16 }}></i>
      </span>
      <span>{t(`Lista dispozicija`)}</span>
    </div>
  );

  const footer = (
    <div className="footer-buttons">
      <ButtonInput
        labelText={t("Zatvori")}
        onButtonClick={handleClose}
        addStyle={addButtonInactiveStyle}
      />
    </div>
  );

  const exportExcel = (disposition: Disposition) => {
    const isMercantile =
      disposition.request?.product?.type === ProductType.MercantileGoods;
    const excelDispositionsObject = {
      Datum: `${Utility.formatDate(new Date(disposition.dateFrom as Date))}`,
      Broj: disposition?.dispositionNumberFormatted,
      Roba: isMercantile
        ? (disposition.request?.product?.name ?? "")
        : getReproNames(disposition),
      UtovarenaKoličina: isMercantile
        ? `${disposition?.mercantileAmount ?? ""} ${
            disposition.request?.product?.measuringUnit ?? "kg"
          }`
        : getReproAmounts(disposition),

      MestoUtovara: `${selectedRequest?.place}`,
      MestoIstovara: `${
        selectedRequest?.type === RequestType.Import ||
        selectedRequest?.type === RequestType.Relocation
          ? disposition.inputLocation
          : disposition?.outputLocation
      }`,
      Vozilo: `${disposition.registrationNumber?.toUpperCase()}`,
      Vozač: `${disposition.driverName}`,
    };
    const excelObject = [
      {
        Datum: "",
        Broj: "",
        Roba: "",
        UtovarenaKoličina: "",
        MestoUtovara: "",
        MestoIstovara: "",
        Vozilo: "",
        Vozač: "",
      },
      { ...excelDispositionsObject },
    ];
    import("xlsx").then((xlsx) => {
      const worksheet = xlsx.utils.json_to_sheet(excelObject);
      const workbook = { Sheets: { data: worksheet }, SheetNames: ["data"] };
      const excelBuffer = xlsx.write(workbook, {
        bookType: "xlsx",
        type: "array",
      });
      saveAsExcelFile(excelBuffer, "Dispozicija za rad");
    });
  };

  const saveAsExcelFile = (buffer: any, fileName: any) => {
    import("file-saver").then((module: any) => {
      if (module && module.default) {
        let EXCEL_TYPE =
          "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
        let EXCEL_EXTENSION = ".xlsx";
        const data = new Blob([buffer], {
          type: EXCEL_TYPE,
        });
        module.default.saveAs(data, fileName + EXCEL_EXTENSION);
      }
    });
  };

  const onHideModal = () => {
    setPdfData(undefined);
    setSelectedRequest(undefined);
    setDispositionsByRequest([]);
    handleClose();
  };

  return (
    <>
      <Dialog
        header={header}
        visible={visible}
        style={{ width: "65vw" }}
        className="dispositions-list-modal"
        footer={footer}
        blockScroll={false}
        draggable={false}
        onHide={onHideModal}
      >
        <div className="dispositions-list-body">
          {processing ? (
            <ProgressSpinner />
          ) : (
            <table className="form-table-wrapper">
              <tr className="form-table-row bold-font">
                <th className="header-style">{t("Datum")}</th>
                <th className="header-style">{t("Broj")}</th>
                <th className="header-style">{t("Roba")}</th>
                <th className="header-style">{t("Utovarena količina")}</th>
                <th className="header-style">{t("Mesto utovara")}</th>
                <th className="header-style">{t("Mesto istovara")}</th>
                <th className="header-style">{t("Vozilo")}</th>
                <th className="header-style">{t("Vozač")}</th>
                <th className="header-style"></th>
              </tr>           
              {dispositionsByRequest.map((disposition) => (
                <tr className="form-table-row">
                  <td className="header-style">
                    {Utility.formatDate(new Date(disposition.dateFrom as Date))}
                  </td>
                  <td className="header-style">{disposition?.dispositionNumberFormatted}</td>
                  <td className="header-style">
                    {disposition.request?.product?.type ===
                    ProductType.MercantileGoods
                      ? disposition.request.product.name
                      : getReproNames(disposition)}
                  </td>
                  <td className="header-style">
                    {disposition.request?.product?.type ===
                    ProductType.MercantileGoods
                      ? `${disposition.mercantileAmount} kg`
                      : getReproAmounts(disposition)}
                  </td>
                  <td className="header-style">{selectedRequest?.place}</td>
                  <td className="header-style">
                    {selectedRequest?.type === RequestType.Import ||
                    selectedRequest?.type === RequestType.Relocation
                      ? disposition.inputLocation
                      : disposition?.outputLocation}
                  </td>
                  <td className="header-style">
                    {disposition.registrationNumber?.toUpperCase()}
                  </td>
                  <td className="header-style">{disposition.driverName}</td>
                  <td className="action-column">
                    <ButtonInput
                      addStyle={{
                        border: "none",
                        background: "#198754",
                        marginRight: "5px",
                      }}
                      labelText={""}
                      icon="bi bi-file-earmark-excel"
                      disabled={
                        disposition.request?.product?.type !==
                          ProductType.MercantileGoods &&
                        productsLocal.length === 0
                      }
                      onButtonClick={() => exportExcel(disposition)}
                    />
                    <ButtonInput
                      addStyle={{ border: "none", background: "#198754" }}
                      labelText={""}
                      icon="bi bi-printer"
                      onButtonClick={() => {
                        const isMercantile =
                          disposition.request?.product?.type ===
                          ProductType.MercantileGoods;
                        const placeText = selectedRequest?.place ?? "";
                        const locationText =
                          selectedRequest?.type === RequestType.Import ||
                          selectedRequest?.type === RequestType.Relocation
                            ? (disposition.inputLocation ?? "")
                            : (disposition.outputLocation ?? "");
                        const robaForPdf = isMercantile
                          ? (disposition.request?.product?.name ?? "")
                          : getReproNamesArray(disposition);
                        const amountForPdf = isMercantile
                          ? `${disposition?.mercantileAmount ?? ""} ${
                              disposition.request?.product?.measuringUnit ??
                              "kg"
                            }`
                          : getReproAmountsArray(disposition);
                        const newPdf =
                          getDispositionsByUnfinishedRequestTemplate(
                            Utility.formatDate(
                              new Date(disposition.dateFrom as Date),
                            ),
                            disposition?.dispositionNumberFormatted ?? '',
                            robaForPdf as any,
                            amountForPdf as any,
                            placeText,
                            locationText,
                            disposition.registrationNumber?.toUpperCase() ?? "",
                            disposition.driverName ?? "",
                            isMercantile,
                          );
                        setPdfData(newPdf);
                      }}
                    />
                  </td>
                </tr>
              ))}
            </table>
          )}
        </div>
        <div className="document-preview">
          <PdfPreview
            pdfDefinition={pdfData}
            downloadDocument={downloadPdfDocument}
            printDocument={printPdfDocument}
            documentName={`${t("Dispozicija za rad ")}_${new Date().getTime()}`}
          />
        </div>
      </Dialog>
    </>
  );
};
